import { useMemo } from "react"
import { Form, Formik } from "formik"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../../modules/localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"

import { SelectedMainUserState } from "../../createOrderTypes"
import { MeetingServiceFormContent } from "./MeetingServiceFormContent"
import { mergeValuesWithConfig } from "../form/mergeValuesWithConfig"
import { generateValidationSchema } from "../form/formLib"
import { useCreateOrderFormConfig } from "../../mainForm/CreateOrderFormContext"
import { OrderContextState } from "../../mainForm/helpers"
import {
    DynamicMainFormValues,
    MeetingServiceConfig,
    MainOrderFormikRef,
    MeetingServiceTabValues,
} from "../form/formTypes"

import { MEETING_SERVICE_FORM_ID } from "./helpers"

const useStyles = makeStyles()(() => ({
    form: {
        width: "100%",
    },
}))

type MeetingServiceFormProps = Readonly<{
    user: SelectedMainUserState
    handleClose: () => void
    handleOpenSelectPriceListsDialog: () => void
    meetingServiceConfiguration?: MeetingServiceConfig
    orderDetails?: MeetingServiceOrder
    mainValues: DynamicMainFormValues
    orderContext: OrderContextState
    handleSubmitMeetingService: (values: MeetingServiceTabValues) => void
    outerFormik: MainOrderFormikRef
}>

export function MeetingServiceForm({
    user,
    handleClose,
    handleOpenSelectPriceListsDialog,
    meetingServiceConfiguration,
    orderDetails,
    mainValues,
    orderContext,
    handleSubmitMeetingService,
    outerFormik,
}: MeetingServiceFormProps) {
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()
    const { classes } = useStyles()
    const { dialogType, flowConfig } = useCreateOrderFormConfig()

    const validationSchema = useMemo(
        () => generateValidationSchema(dialogType, l10n, meetingServiceConfiguration),
        [dialogType, l10n, meetingServiceConfiguration],
    )

    // Seed the inner Formik only when the config (or other static seed inputs) actually changes.
    // `mainValues` is intentionally excluded from deps: the inner form syncs its own values back to the
    // outer form (debounced in MeetingServiceFormContent), so reacting to `mainValues` would recompute
    // `initialValues` and, with `enableReinitialize`, reset `touched` on every keystroke — which is what
    // made regex validation errors flicker and reverted predefined-value deletions.
    const initialValues = useMemo(
        () =>
            mergeValuesWithConfig({
                existing: mainValues,
                config: meetingServiceConfiguration,
                user,
                dateTimeFactory,
                dialogType,
                orderDetails,
                mode: orderContext.mode,
                flowConfig,
            }) as MeetingServiceTabValues,
        [meetingServiceConfiguration, orderDetails, user, dateTimeFactory, dialogType, orderContext.mode, flowConfig],
    )

    return (
        <Formik
            initialValues={initialValues}
            validateOnBlur={false}
            validationSchema={validationSchema}
            onSubmit={handleSubmitMeetingService}
            enableReinitialize
        >
            <Form id={MEETING_SERVICE_FORM_ID} className={classes.form}>
                <MeetingServiceFormContent
                    user={user}
                    handleClose={handleClose}
                    handleOpenSelectPriceListsDialog={handleOpenSelectPriceListsDialog}
                    meetingServiceConfiguration={meetingServiceConfiguration}
                    outerFormik={outerFormik}
                    mode={orderContext.mode}
                    orderId={orderContext.orderId}
                />
            </Form>
        </Formik>
    )
}
